import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError, setUnauthorizedHandler } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Register global 401 handler: kick user out + redirect once initial auth check is done
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!initialized.current) return;
      setUser((prev) => {
        if (prev) {
          // Only show toast + redirect if we previously thought we were logged in
          if (typeof window !== "undefined" && window.location.pathname !== "/login") {
            try { sessionStorage.setItem("session_expired", "1"); } catch {}
            window.location.assign("/login");
          }
        }
        return null;
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  const refresh = async () => {
    try {
      await api.post("/auth/refresh");
    } catch {
      setUser(null);
    }
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthCtx.Provider
      value={{ user, loading, login, register, logout, refresh, updateProfile, formatApiError, setUser }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
